import React, { useCallback, useMemo, useState } from "react"
import { Line } from "react-chartjs-2"
import { convertToDashedDate, formatDate, toYYYYMMDD, withAlpha } from "../../../../utility"
import { colorPaletteOptions } from "../../../../constants"
import { lang } from "../../../../constants/languages"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRange/KSelectRange"

type HoveredIndex = { datasetIndex: number; dataIndex: number }

const parseLocalDate = (dashed: string) => new Date(`${dashed}T00:00:00`)
const toDashed = (date: Date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}
const addDays = (date: Date, days: number) => {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

const WEEKDAY = [
  lang.common.week_days_short.sun,
  lang.common.week_days_short.mon,
  lang.common.week_days_short.tue,
  lang.common.week_days_short.wed,
  lang.common.week_days_short.thu,
  lang.common.week_days_short.fri,
  lang.common.week_days_short.sat
] as const
const getWeekday = (dashed: string) => WEEKDAY[parseLocalDate(dashed).getDay()]

const TrendChart = ({ data, dateValues, title, lineColor }: { data: any[], dateValues: DateRangeType, title?:string, lineColor?: string}) => {
  const [hoveredIndex, setHoveredIndex] = useState<HoveredIndex>({
    datasetIndex: -1,
    dataIndex: -1
  })


  const betweenPointsLinePlugin = {
    id: "betweenPointsLine",

    afterDraw: (chart: any) => {
      const tooltip = chart?.tooltip

      if (!tooltip?.getActiveElements) return
      const active = tooltip.getActiveElements()


      if (!active || active.length < 2) return
      const { ctx } = chart

      const x = active[0].element?.x
      const ys = active.map((a: any) => a.element?.y).filter((y: any) => typeof y === "number")

      if (typeof x !== "number" || ys.length < 2) return

      const yMin = Math.min(...ys)
      const yMax = Math.max(...ys)
      ctx.save()
      ctx.beginPath()
      ctx.moveTo(x, yMin)
      ctx.lineTo(x, yMax)
      ctx.lineWidth = 2
      ctx.strokeStyle = "rgba(17, 24, 39, 0.32)" 
      ctx.stroke()
      ctx.restore()
    }
  }

  const handleHover = useCallback((_evt: unknown, elements: any[]) => {
    setHoveredIndex((prev: HoveredIndex) => {
      if (elements.length) {
        const { datasetIndex, index: dataIndex } = elements[0]
        if (datasetIndex === prev.datasetIndex && dataIndex === prev.dataIndex) return prev
        return { datasetIndex, dataIndex }
      }
      if (prev.datasetIndex === -1 && prev.dataIndex === -1) return prev
      return { datasetIndex: -1, dataIndex: -1 }
    })
  }, [])

  const baseChart = useMemo(() => {
    const totalsByDate: Record<string, number> = {}

    data.forEach((item) => {
      const date = convertToDashedDate(item.date)
      totalsByDate[date] = (totalsByDate[date] ?? 0) + (item.streams ?? 0)
    })

    const dates = dateValues as Date[]

    if (!dates?.[1]) {
      return { labels: [] as string[], datasets: [] as any[] }
    }

    const maxDateStr = convertToDashedDate(toYYYYMMDD(dates?.[1]))

    const maxDate = parseLocalDate(maxDateStr)

    const thisWeekDates = Array.from({ length: 7 }, (_, i) => toDashed(addDays(maxDate, -6 + i)))
    const prevWeekEnd = addDays(maxDate, -7)
    const prevWeekDates = Array.from({ length: 7 }, (_, i) => toDashed(addDays(prevWeekEnd, -6 + i)))

    const thisWeekValues: number[] = thisWeekDates.map((ds) => totalsByDate[ds] ?? 0)
    const prevWeekValues: number[] = prevWeekDates.map((ds) => totalsByDate[ds] ?? 0)

    const labels = thisWeekDates.map(getWeekday)

    const thisWeekLine = lineColor || colorPaletteOptions[0] 
    const prevWeekLine = "#8B8B8B"

    const pointStyle = {
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHitRadius: 12,
      pointHoverBorderWidth: 0,
      pointHoverBackgroundColor: "" 
    }

    return {
      labels,
      datasets: [
        {
          label: lang.trends.selected_week,
          data: thisWeekValues,
          metaDates: thisWeekDates,
          borderColor: thisWeekLine,
          backgroundColor: withAlpha(thisWeekLine, 0.14), 
          fill: "origin" as const,
          tension: 0.4,
          borderWidth: 2,
          spanGaps: true,
          ...pointStyle,
          pointHoverBackgroundColor: thisWeekLine 
        },
        {
          label: lang.trends.previous_week,
          data: prevWeekValues,
          metaDates: prevWeekDates,
          borderColor: prevWeekLine,
          backgroundColor: "transparent",
          fill: false,
          tension: 0.4,
          borderWidth: 2,
          spanGaps: true,
          ...pointStyle,
          pointHoverBackgroundColor: prevWeekLine 
        }
      ]
    }
  }, [data]) 

  const chartData = useMemo(
    () => ({
      labels: baseChart.labels,
      datasets: baseChart.datasets
    }),
    [baseChart]
  )

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      clip: false as const,

      interaction: { intersect: false, mode: "index" as const },

      plugins: {
        datalabels: { display: false },

        tooltip: {
          displayColors: false,
          usePointStyle: true,
          backgroundColor: "#333",
          xAlign: "center" as const,
          yAlign: "bottom" as const,
          caretSize: 0,
          caretPadding: 10,
          padding: 10,
          titleFont: { family: "Inter", weight: 600, size: 14, lineHeight: "16px" },
          bodyFont: { family: "Inter", weight: 400, size: 12, lineHeight: "16px" },
          callbacks: {
            label: (ctx: any) => {
              const v = ctx.parsed?.y ?? 0
              const dateStr = ctx.dataset?.metaDates?.[ctx.dataIndex].replaceAll("-", "")
              const formattedDateStr = formatDate(dateStr)
              //return `${ctx.dataset.label} (${dateStr}): ${Number(v).toLocaleString()}`
              return `${formattedDateStr}:    ${Number(v).toLocaleString()}`
            }
          }
        },

        hover: { intersect: false },

        legend: {
          display: true,
          position: "top" as const,
          align: "end" as const,
          labels: {
            font: { family: "Inter", weight: 500, size: 12, lineHeight: "12px" },
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 8,
            boxHeight: 8,
            generateLabels: (chart: any) => {
              const datasets = chart.data.datasets
              return datasets.map((dataset: any, i: number) => ({
                text: dataset.label,
                fillStyle: dataset.borderColor,
                strokeStyle: dataset.borderColor,
                lineWidth: 0,
                hidden: !chart.isDatasetVisible(i),
                datasetIndex: i,
                fontColor: "#666"
              }))
            }
          }
        }
      },

      scales: {
        y: {
          grid: {
            display: true,
            color: () => "transparent",
            drawTicks: true
          },
          border: { display: true },
          ticks: {
            color: "#999",
            font: { family: "Inter", weight: 500, size: 11, lineHeight: "12px" }
          }
        },
        x: {
          grid: { display: true, drawBorder: true, drawTicks: true, drawOnChartArea: false },
          border: { display: true },
          ticks: { color: "#999", font: { family: "Inter", weight: 500, size: 11, lineHeight: "12px" } }
        }
      },

      onHover: handleHover
    }),
    [handleHover]
  )

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        padding: 10,
        borderRadius: "10px",
        border: "1px solid var(--stroke-soft-200, #F3F3F3)",
        background: " var(--bg-white-0, #FFF)",
        boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
      }}
    >
      <div style={{ width: "100%", height: "5%", paddingLeft: 12, paddingTop: 4 }}>
        <KTitleSpan text={title || lang.trends.headers.trend_snapshot} fontSize={16} color="#737373" lineHeight="20px" fontWeight={500} />
      </div>
      <div style={{ width: "100%", height: "95%", padding: 20 }}>
        <Line data={chartData} options={options}  plugins={[betweenPointsLinePlugin]} style={{ width: "100%", height: "100%" }} />
      </div>
    </div>
  )
}

export default TrendChart
